import { Link } from "react-router-dom";

// DRAFT — LEGAL REVIEW REQUIRED. This document is a beta-launch
// starting point for Subtend Studio, an Australian B2B SaaS product.
// It must be reviewed by an Australian commercial lawyer before general
// availability. Do not represent this content as legal advice.
//
// LEGAL ENTITY DECISION REQUIRED: the trading name "Subtend Studio" is
// the product/service brand. The legal contracting entity behind it
// (registered ABN + business name) has NOT yet been decided at the
// time of drafting. Every reference below to "[Legal Entity]" is
// deliberately parameterised so the owner can substitute the correct
// legal name in one place.
const LEGAL_ENTITY = "[Legal Entity — LEGAL REVIEW REQUIRED]";
const LEGAL_EMAIL_CONTACT = "legal@luxemediawalls.com";      // pending domain migration
const LEGAL_PRIVACY_EMAIL = "privacy@luxemediawalls.com";    // pending domain migration

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="terms-page">
        <div className="overline mb-4">Legal · Draft</div>
        <h1 className="font-serif font-light text-4xl mb-4">Terms of Service</h1>
        <p className="text-xs text-muted-foreground mb-8">
          Last updated: 14 February 2026 · Beta programme edition ·
          Draft for legal review.
        </p>

        <div className="border-l-2 border-[color:hsl(var(--accent))] pl-4 py-3 mb-10 bg-[color:hsl(var(--accent)/0.06)]">
          <div className="overline text-[10px] text-[color:hsl(var(--accent))] mb-1">Legal review required</div>
          <div className="text-xs text-muted-foreground leading-relaxed">
            The service is now presented as <strong>Subtend Studio</strong>. The
            legal contracting entity, ABN, registered business name and email
            domain remain to be confirmed by the owner before general
            availability. Every occurrence of <code>{"{" + "Legal Entity" + "}"}</code> below is a
            placeholder awaiting that decision.
          </div>
        </div>

        <Section title="1. About Subtend Studio">
          {LEGAL_ENTITY} operates Subtend Studio (“Subtend Studio”, the
          “Service”, “we”, “us”), a software-as-a-service workspace for
          media-wall, cabinetry and AV design businesses (“you”, “Customer”).
          By registering an account or using the Service you agree to these
          Terms of Service.
        </Section>

        <Section title="2. Beta programme">
          Subtend Studio is currently offered as a controlled beta programme to
          a limited number of businesses. Availability, features and pricing
          may change during the beta with reasonable notice. You may terminate
          your beta subscription at any time on 30 days' notice.
        </Section>

        <Section title="3. Business users">
          The Service is provided to businesses only. You represent that you are
          authorised to bind the business you register. You are responsible for
          the acts and omissions of users you invite to your workspace.
        </Section>

        <Section title="4. Account responsibility">
          You are responsible for keeping your credentials secure and for all
          activity that occurs under your account. You will notify us promptly
          if you suspect unauthorised access.
        </Section>

        <Section title="5. Acceptable use">
          You will not use the Service to store or transmit unlawful content,
          infringe another party's rights, reverse engineer or resell the
          Service without our prior written consent, or attempt to access
          another customer's data.
        </Section>

        <Section title="6. Subscription and payment">
          During the beta programme, subscription fees are invoiced manually.
          Invoices are payable within 14 days. Continued non-payment may result
          in suspension or termination of your account. Prices are in
          Australian dollars and exclude GST unless stated.
        </Section>

        <Section title="7. Intellectual property">
          {LEGAL_ENTITY} and its licensors own all rights, title and interest in
          the Service, the Subtend Studio brand and the starter content shipped
          with the Service (materials, design templates, imagery). You retain
          all rights in the Customer Data you submit. You grant {LEGAL_ENTITY}
          a limited licence to host, process and display your Customer Data for
          the purpose of providing the Service.
        </Section>

        <Section title="8. Customer-uploaded content">
          You are responsible for the content you upload (photos, files,
          project data, client information). You represent that you have the
          rights to upload it and to permit us to process it as described in
          the Privacy Policy.
        </Section>

        <Section title="9. Generated designs">
          Designs, quotes and proposals produced with the Service belong to
          you. {LEGAL_ENTITY} does not claim ownership of your project output.
          {LEGAL_ENTITY} may use anonymised, aggregated usage data to improve
          the Service.
        </Section>

        <Section title="10. Third-party services">
          The Service uses third-party processors, currently including but
          not limited to Emergent Platform (hosting + database + object
          storage) and Resend (transactional email). Their processing is
          subject to their own terms. See the Privacy Policy for details.
        </Section>

        <Section title="11. Service availability">
          We strive for high availability but do not guarantee uninterrupted
          operation during the beta programme. Planned maintenance will be
          announced with reasonable notice where practical.
        </Section>

        <Section title="12. Limitation of liability">
          To the maximum extent permitted by law, {LEGAL_ENTITY}'s aggregate
          liability in connection with the Service is limited to the fees paid
          by you in the twelve months preceding the event giving rise to the
          claim. Nothing in these Terms excludes liability that cannot be
          excluded under the Australian Consumer Law.
        </Section>

        <Section title="13. Termination">
          Either party may terminate for material breach not remedied within
          30 days of written notice. On termination, we will make your
          Customer Data available for export for 30 days, after which it will
          be deleted from active systems (subject to backup retention).
        </Section>

        <Section title="14. Governing law">
          These Terms are governed by the laws of New South Wales, Australia.
          The parties submit to the non-exclusive jurisdiction of the courts
          of that state.
        </Section>

        <Section title="15. Contact">
          Questions about these Terms may be sent to {LEGAL_EMAIL_CONTACT}.
          Email domain migration is pending owner decision — see the header
          notice above.
        </Section>

        <div className="mt-16 text-xs text-muted-foreground">
          <Link to="/privacy" className="underline" data-testid="link-privacy-from-terms">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif text-xl mb-3">{title}</h2>
      <div className="text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}
